import { useState } from 'react';
import Icon from '../../components/AppIcon';
import ReportTemplateCard from './components/ReportTemplateCard';
import ReportCanvas from './components/ReportCanvas';
import ParameterPanel from './components/ParameterPanel';
import SavedReportsList from './components/SavedReportsList';
import WidgetLibrary from './components/WidgetLibrary';
import ScheduleModal from './components/ScheduleModal';
import TopNavbar from '../../components/ui/TopNavbar';

const ReportsAndAnalytics = () => {
  const [activeTab, setActiveTab] = useState('templates');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [parameters, setParameters] = useState({});
  const [showMobileTemplates, setShowMobileTemplates] = useState(false);
  const [showMobileWidgets, setShowMobileWidgets] = useState(false);

  const reportTemplates = [
    {
      id: 1,
      name: 'Shipment Performance Report',
      category: 'operational',
      icon: 'Ship',
      description: 'Comprehensive analysis of shipment volumes, delays, and port performance metrics',
      lastUsed: '2 days ago',
      usageCount: 45,
    },
    {
      id: 2,
      name: 'Cost Analysis Dashboard',
      category: 'financial',
      icon: 'DollarSign',
      description: 'Detailed breakdown of inspection, customs, and operational costs by supplier',
      lastUsed: '5 days ago',
      usageCount: 32,
    },
    {
      id: 3,
      name: 'Fee Reconciliation Report',
      category: 'financial',
      icon: 'Receipt',
      description: 'Monthly fee calculations with advance payments and reimbursable cost tracking',
      lastUsed: '1 week ago',
      usageCount: 28,
    },
    {
      id: 4,
      name: 'Issue Resolution Tracker',
      category: 'operational',
      icon: 'AlertTriangle',
      description: 'Status tracking of open issues with severity analysis and resolution timelines',
      lastUsed: '3 days ago',
      usageCount: 38,
    },
    {
      id: 5,
      name: 'Compliance Audit Report',
      category: 'compliance',
      icon: 'Shield',
      description: 'Regulatory compliance status with document verification and audit trail',
      lastUsed: '2 weeks ago',
      usageCount: 15,
    },
    {
      id: 6,
      name: 'Supplier Performance Scorecard',
      category: 'operational',
      icon: 'TrendingUp',
      description: 'Comparative analysis of supplier reliability, quality, and delivery performance',
      lastUsed: '4 days ago',
      usageCount: 41,
    },
  ];

  const savedReports = [
    {
      id: 1,
      name: 'Q4 2024 Operations Summary',
      description: 'Quarterly operational review with KPIs',
      createdBy: 'Sarah Chen',
      lastRun: '12 Dec 2024',
      scheduled: true,
      frequency: 'monthly',
    },
    {
      id: 2,
      name: 'Weekly Shipment Status',
      description: 'Active shipments and pending deliveries',
      createdBy: 'Michael Rodriguez',
      lastRun: '10 Dec 2024',
      scheduled: true,
      frequency: 'weekly',
    },
    {
      id: 3,
      name: 'Cost Variance Analysis',
      description: 'Budget vs actual cost comparison',
      createdBy: 'Emily Watson',
      lastRun: '08 Dec 2024',
      scheduled: false,
      frequency: null,
    },
  ];

  const handleGenerateReport = (template) => {
    console.log('Generating report:', template);
    setIsGenerating(true);
    setTimeout(() => {
      setIsGenerating(false);
      alert(`✅ ${template?.name} report generated!`);
    }, 2000);
  };

  const handleSaveReport = (reportData) => {
    console.log('Saving report:', reportData);
    // TODO: Implement report saving
    alert('✅ Report saved successfully!');
  };

  const handleScheduleReport = (reportId, schedule) => {
    console.log('Scheduling report:', reportId, schedule);
    setShowScheduleModal(true);
  };

  const handleSaveSchedule = (scheduleData) => {
    console.log('Saving schedule:', scheduleData);
    // TODO: Implement schedule saving
    alert('✅ Report schedule configured!');
    setShowScheduleModal(false);
  };

  const handleSchedule = (scheduleData) => {
    console.log('Handling schedule:', scheduleData);
    handleSaveSchedule(scheduleData);
  };

  const handleExportReport = (reportId, format) => {
    console.log('Exporting report:', reportId, format);
    // TODO: Implement export
    alert(`✅ Exporting report as ${format}...`);
  };

  const handleAddWidget = (widgetType) => {
    console.log('Adding widget:', widgetType);
    // TODO: Implement widget addition
    alert(`✅ ${widgetType} widget added to canvas!`);
  };

  const handleRemoveWidget = (widgetId) => {
    console.log('Removing widget:', widgetId);
    // TODO: Implement widget removal
    alert('✅ Widget removed!');
  };

  const handleUpdateParameter = (paramName, value) => {
    console.log('Updating parameter:', paramName, value);
    setParameters(prev => ({ ...prev, [paramName]: value }));
  };

  const handleDeleteReport = (reportId) => {
    if (confirm('Are you sure you want to delete this report?')) {
      console.log('Deleting report:', reportId);
      // TODO: Implement deletion
      alert('✅ Report deleted!');
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <TopNavbar />
      <div className="flex-1 flex flex-col overflow-hidden mt-[65px]">
        {/* Mobile-optimized header */}
        <header className="bg-card border-b border-border px-3 sm:px-4 md:px-6 py-3 sm:py-4 sticky top-[65px] z-20">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-foreground truncate">
                  Reports & Analytics
                </h1>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1 line-clamp-2">
                  Generate and analyze business reports
                </p>
              </div>
              
              <div className="flex items-center gap-2 flex-wrap">
                <button className="px-3 sm:px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2 text-xs sm:text-sm min-h-[44px] sm:min-h-auto flex-1 sm:flex-initial justify-center">
                  <Icon name="Plus" size={16} />
                  <span>New Report</span>
                </button>
                <button className="px-3 sm:px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/90 transition-colors flex items-center gap-2 text-xs sm:text-sm min-h-[44px] sm:min-h-auto">
                  <Icon name="Filter" size={16} />
                  <span className="hidden sm:inline">Filter</span>
                </button>
              </div>
            </div>

            {/* Quick stats - responsive grid */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-2 sm:p-3 border border-blue-200">
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <div className="bg-blue-600 rounded-lg p-1.5 sm:p-2 w-fit">
                    <Icon name="TrendingUp" size={14} className="text-white sm:w-4 sm:h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-blue-700 font-medium truncate">Templates</p>
                    <p className="text-sm sm:text-lg font-bold text-blue-900">45</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-2 sm:p-3 border border-green-200">
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <div className="bg-green-600 rounded-lg p-1.5 sm:p-2 w-fit">
                    <Icon name="DollarSign" size={14} className="text-white sm:w-4 sm:h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-green-700 font-medium truncate">Spend</p>
                    <p className="text-sm sm:text-lg font-bold text-green-900">$12.5K</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-2 sm:p-3 border border-orange-200">
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <div className="bg-orange-600 rounded-lg p-1.5 sm:p-2 w-fit">
                    <Icon name="AlertTriangle" size={14} className="text-white sm:w-4 sm:h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-orange-700 font-medium truncate">Actions</p>
                    <p className="text-sm sm:text-lg font-bold text-orange-900">3</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main content */}
        <div className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
          {/* Mobile: Stack vertically with collapsible sections, Desktop: 3-column layout */}
          <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 h-full">
            {/* Templates/Saved Reports - collapsible on mobile */}
            <div className="w-full lg:w-1/5">
              {/* Mobile toggle button */}
              <button
                onClick={() => setShowMobileTemplates(!showMobileTemplates)}
                className="lg:hidden w-full bg-card rounded-lg border border-border p-3 flex items-center justify-between mb-2"
              >
                <span className="font-semibold text-sm">Templates & Reports</span>
                <Icon name={showMobileTemplates ? "ChevronUp" : "ChevronDown"} size={20} />
              </button>

              {/* Collapsible content on mobile, always visible on desktop */}
              <div className={`bg-card rounded-lg border border-border overflow-hidden flex-col ${showMobileTemplates ? 'flex' : 'hidden lg:flex'}`}>
                <div className="flex border-b border-border">
                  <button
                    onClick={() => setActiveTab('templates')}
                    className={`flex-1 px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-colors ${
                      activeTab === 'templates' ? 'bg-primary/10 text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Templates
                  </button>
                  <button
                    onClick={() => setActiveTab('saved')}
                    className={`flex-1 px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-colors ${
                      activeTab === 'saved' ? 'bg-primary/10 text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Saved
                  </button>
                </div>

                <div className="overflow-y-auto p-3 sm:p-4 max-h-[400px] lg:max-h-[calc(100vh-350px)]">
                  {activeTab === 'templates' ? (
                    <div className="space-y-2 sm:space-y-3">
                      {reportTemplates?.map((template) => (
                        <ReportTemplateCard
                          key={template?.id}
                          template={template}
                          onSelect={setSelectedTemplate}
                          isSelected={selectedTemplate?.id === template?.id}
                        />
                      ))}
                    </div>
                  ) : (
                    <SavedReportsList
                      reports={savedReports}
                      onSelect={setSelectedReport}
                      selectedReport={selectedReport}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Report Canvas - main content area */}
            <div className="flex-1 bg-card rounded-lg border border-border overflow-hidden min-h-[400px] lg:min-h-0">
              <ReportCanvas
                selectedTemplate={selectedTemplate}
                widgets={widgets}
                onAddWidget={handleAddWidget}
                onRemoveWidget={handleRemoveWidget}
              />
            </div>

            {/* Widgets and Parameters - collapsible on mobile */}
            <div className="w-full lg:w-1/5 flex flex-col gap-3 sm:gap-4">
              {/* Widgets section */}
              <div className="w-full">
                {/* Mobile toggle button */}
                <button
                  onClick={() => setShowMobileWidgets(!showMobileWidgets)}
                  className="lg:hidden w-full bg-card rounded-lg border border-border p-3 flex items-center justify-between mb-2"
                >
                  <span className="font-semibold text-sm flex items-center gap-2">
                    <Icon name="Package" size={18} />
                    Widgets
                  </span>
                  <Icon name={showMobileWidgets ? "ChevronUp" : "ChevronDown"} size={20} />
                </button>

                {/* Collapsible content on mobile, always visible on desktop */}
                <div className={`bg-card rounded-lg border border-border overflow-hidden ${showMobileWidgets ? 'block' : 'hidden lg:block'}`}>
                  <div className="p-3 sm:p-4 border-b border-border">
                    <h3 className="font-semibold text-sm sm:text-base text-foreground flex items-center gap-2">
                      <Icon name="Package" size={18} className="sm:w-5 sm:h-5" />
                      <span>Widgets</span>
                    </h3>
                  </div>
                  <div className="p-3 sm:p-4 overflow-y-auto max-h-[300px] lg:max-h-[calc(50vh-150px)]">
                    <WidgetLibrary onWidgetDragStart={(widget) => console.log('Dragging:', widget)} />
                  </div>
                </div>
              </div>

              {/* Parameters Panel */}
              <div className="bg-card rounded-lg border border-border overflow-hidden">
                <ParameterPanel
                  onExport={handleExportReport}
                  onSchedule={() => setIsScheduleModalOpen(true)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <ScheduleModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        onSchedule={handleSchedule}
      />
    </div>
  );
};

export default ReportsAndAnalytics;